import { useState, useEffect } from "react";
import ImageCarousel from "@components/features/product-detail/info/ImageCarousel";
import AddButtonSet from "./AddButtonSet";
import ProductConfiguration from "./ProductConfiguration";
import DeliveryOptions from "./DeliveryOptions";
import FlashSale from "./FlashSale";
import WarrantyInfo from "./WarrantyInfo";
import PaymentOptions from "./PaymentOptions";
import RatingStars from "./Ratings";
import SocialShare from "./SocialShare";
import StockStatus from "./StockStatus";
import ProductSpecific from "../specification/ProductSpecTable";
import { errorToast } from "@utils/toast";
import {
  ProductDetail,
  ProductMedia,
  CustomizeMediagallery,
  CustomizeValue,
} from "@interfaces/dto/product-detail.dto";
import { CartItemDTO, CartSimpleItem } from "@interfaces/dto/cart-item.dto";
import { numberToMoneyString } from "@utils/numbers.util";
import { ProductImage } from "@interfaces/components/product-image.interface";
import { max } from "underscore";
import { useProductWishlistChecked } from "@api/products/products";
import { commonToast } from "@utils/toast";
import { updateProductWishlist } from "@api/customer/wishlist";
import { useRouter } from "next/router";
import { getCartId, addItemToCart } from "@api/customer/cart";
import Modal from "react-bootstrap/Modal";
import AddToCartModal from "./AddToCartModal";
import Link from "next/link";

import { useSelector } from "react-redux";
import { isAuthenticatedSelector } from "@stores/slices/userSlice";
import ProductViewNavigate from "@components/features/navigation/ProductViewNavigate";
import {
  isWebview,
  updateCartCountNative,
  updateWishListCountNative,
} from "@utils/webview.util";
import SliderSync from "./SliderSync";

interface Props {
  productKey: string | string[] | undefined;
  productDetail: ProductDetail;
}

const ProductInfo = ({ productKey, productDetail }: Props) => {
  const router = useRouter();
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  // Product configurable state handler, Varient selected changed
  const [selectedVarientSku, setSelectedVarientSku] = useState<string>("");
  const [maximumQty, setMaximumQty] = useState<number>(10);
  const [isSaleable, setIsSaleable] = useState<boolean>(true);
  const [isAddtoCartLoading, setAddtoCartLoading] = useState<boolean>(false);
  const [isShowAddtoCartModal, setShowAddtoCartModal] =
    useState<boolean>(false);
  const [productSelectedQty, setProductSelectedQty] = useState<number>(0);
  const [isFixPriceMode, setIsFixPriceMode] = useState<boolean>(true);
  const [fixPriceValue, setFixPriceValue] = useState<number>(0);
  const [specialPriceValue, setSpecialPriceValue] = useState<number>(0);
  const [minPriceValue, setMinPriceValue] = useState<number>(0);
  const [maxPriceValue, setMaxPriceValue] = useState<number>(0);
  const [isWishlistLoading, setIsWishlistLoading] = useState<boolean>(false);

  const [showCanvas, setShowCanvas] = useState(false);
  const handleShowCanvas = () => setShowCanvas(true);
  const handleCloseCanvas = () => setShowCanvas(false);

  useEffect(() => {
    if (isFixPriceMode) {
      if (selectedVarientSku !== "" && productDetail.customizes.length > 0) {
        const varient = productDetail!.customizes[0].values.find(
          (v) => v.sku == selectedVarientSku
        );
        setFixPriceValue(varient!.price);
        setSpecialPriceValue(varient!.special_price);
      } else {
        setSpecialPriceValue(productDetail.special_price);
        setFixPriceValue(productDetail.price);
      }
    }
  }, [isFixPriceMode, selectedVarientSku, productDetail]);

  useEffect(() => {
    if (
      (productDetail.price_range.price_min !== null &&
        productDetail.price_range.price_min > 0) ||
      (productDetail.price_range.special_price_min !== null &&
        productDetail.price_range.special_price_min > 0)
    ) {
      setIsFixPriceMode(false);
      if (
        productDetail.price_range.special_price_min !== null &&
        productDetail.price_range.special_price_min > 0
      ) {
        setMinPriceValue(productDetail.price_range.special_price_min);
        setMaxPriceValue(productDetail.price_range.special_price_max);
      } else {
        setMinPriceValue(productDetail.price_range.price_min);
        setMaxPriceValue(productDetail.price_range.price_max);
      }
    }
  }, [
    productDetail.price_range.special_price_max,
    productDetail.price_range.special_price_min,
    productDetail.price_range.price_max,
    productDetail.price_range.price_min,
  ]);
  // Check if product is on flashsale
  const isFlashSale = productDetail.promotion === "flashsale" ? true : false;
  // Calculate special price
  const isSpecialPrice =
    productDetail.price > productDetail.special_price ? true : false;

  const percentOff = isSpecialPrice
    ? 100 -
      Math.floor((productDetail.special_price * 100) / productDetail.price)
    : 0;
  // Logic between type_id simple and configurable
  const isConfigurable = productDetail.customizes.length > 0;

  // Check product wishlist flag
  const { data: isWishlist, mutate: mutateWishlist } =
    useProductWishlistChecked(isAuthenticated ? productDetail.id : null);

  useEffect(() => {
    setIsSaleable(
      productDetail.stocks.length > 0 &&
        productDetail.stocks[0].saleable !== undefined
        ? productDetail.stocks[0].saleable
        : false
    );
  }, [productDetail.stocks]);

  useEffect(() => {
    if (!isConfigurable) {
      setSelectedVarientSku(productDetail.sku);
    } else {
      const varient = productDetail!.customizes[0].values.find(
        (v) => v.sku == selectedVarientSku
      );
      if (varient !== undefined)
        setMaximumQty(varient.stocks[0].quantity as number);
    }
    setProductselectedQty(1);
  }, [selectedVarientSku, productDetail, isConfigurable]);

  const setProductImages = (media: ProductMedia[]) => {
    return media.map((media: ProductMedia) => {
      // extract youtube video id
      let ytEmbededUrl = "";
      if (
        media.video_content !== undefined &&
        media.video_content.video_url !== ""
      ) {
        const ytId = media.video_content.video_url.replace(
          "https://youtu.be/",
          ""
        );
        ytEmbededUrl = `https://www.youtube.com/embed/${ytId}`;
      }
      return {
        url: media.media_path,
        alt: media.media_path,
        type: media.media_type,
        path: media.media_path,
        video_title: media.video_content?.video_title,
        video_url: ytEmbededUrl,
      };
    });
  };

  const handleSelectedVarientChanged = (sku: string) => {
    setSelectedVarientSku(sku);
    const varient = productDetail!.customizes[0].values.find(
      (v) => v.sku == sku
    );

    if (varient !== undefined) {
      setIsSaleable(
        varient.stocks.length > 0 && varient.stocks[0].saleable !== undefined
          ? varient.stocks[0].saleable
          : false
      );
      setIsFixPriceMode(true);
    }

    productImages = [];
  };

  // Product selected quantity state handler
  const [productselectedQty, setProductselectedQty] = useState<number>(1);
  const handleProductselectedQtyChanged = (qty: number) => {
    if (maximumQty !== undefined) {
      if (productselectedQty + qty <= 0) {
        return;
      } else if (productselectedQty + qty > maximumQty) {
        return;
      } else setProductselectedQty(productselectedQty + qty);
    }
  };

  // product image for <ImageCarousel>
  let productImages: ProductImage[] = [];

  if (!isConfigurable || selectedVarientSku === "") {
    productImages = setProductImages(productDetail!.media);
  } else {
    const varient = productDetail!.customizes[0].values.find(
      (v) => v.sku == selectedVarientSku
    );
    let varientImages: ProductMedia[] | undefined = [];
    varientImages = varient?.mediagallery.map((media) => {
      return { ...media, media_path: media.file };
    });
    productImages = setProductImages(varientImages ? varientImages : []);
  }

  const handleUpdateWishlist = async (isWishlist: boolean) => {
    setIsWishlistLoading(true);
    const { isOk, message } = await updateProductWishlist({
      productId: productDetail.id,
      isWishlist: isWishlist,
    });

    if (isOk) {
      mutateWishlist();
      commonToast({ isOk, message });
      if (isWebview()) {
        updateWishListCountNative();
      }
    }
    setIsWishlistLoading(false);
  };

  const handleAddtoCart = async (qty: number) => {
    setAddtoCartLoading(true);
    setProductSelectedQty(qty);
    // check addToCartApi result if 401 authentication failed
    // then redirect to login page with resume token

    const fetchCartId = async () => await getCartId();
    fetchCartId()
      .then(async (response) => {
        const cartId = Number(response.data);

        let cartItem: CartItemDTO;

        cartItem = {
          cartItem: {
            sku: selectedVarientSku,
            qty,
            quote_id: cartId!.toString(),
          },
        };

        if (isConfigurable) {
          const selectedVarient = productDetail.customizes[0].values.find(
            (varient) => varient.sku == selectedVarientSku
          );
          if (!selectedVarient) {
            errorToast("กรุณาระบุตัวเลือกของสินค้า");
          }
          cartItem = {
            cartItem: {
              sku: productDetail.sku,
              qty,
              quote_id: cartId!.toString(),
              product_option: {
                extension_attributes: {
                  configurable_item_options: [
                    {
                      option_id: productDetail.customizes[0].customize_id,
                      option_value: selectedVarient!.value_id,
                    },
                  ],
                },
              },
            },
          };
        }

        // console.log(cartItem);
        const res = await addItemToCart(cartItem);
        if (cartId > 0 && res.isOk) {
          setShowAddtoCartModal(true);
          if (isWebview()) {
            updateCartCountNative();
          }
        } else {
          errorToast("ไม่สามารถเพิ่มสินค้าลงตะกร้าได้ กรุณาลองใหม่อีกครั้ง");
        }
        setAddtoCartLoading(false);
        setShowCanvas(false);
      })
      .catch((err) => {
        // router.push('/404');
        // console.log('error caugth');
        // console.log(err);
        setAddtoCartLoading(false);
      });
  };

  const handleCloseModal = () => {
    setShowAddtoCartModal(false);
  };

  const scrollToReviewSection = () => {
    const reviewSection = document.getElementById("review-section");

    if (reviewSection) {
      reviewSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12 col-lg-5">
          {/* <ImageCarousel productImages={productImages} /> */}
          <SliderSync productImages={productImages} />
        </div>
        <div className="col-12 col-lg-7 ps-lg-1">
          <h1 className="mt-3 mt-lg-0">{productDetail.name}</h1>
          <div className="d-flex d-flex align-content-start align-items-lg-center flex-wrap my-3">
            <span className="badge text-bg-light">
              SKU :{" "}
              {selectedVarientSku !== ""
                ? selectedVarientSku
                : productDetail.sku}
            </span>
            <span className="border-right">|</span>
            <StockStatus isStockAvailable={isSaleable} />
          </div>
          <div className="d-flex d-flex align-content-start align-items-lg-center flex-wrap my-3">
            <RatingStars starCount={productDetail.review[0].star_rating} />
            <p className="m-0 small">
              {productDetail.review[0].reviews_count} รีวิว
            </p>
            <span className="border-right">|</span>
            <button
              type="button"
              className="small text-decoration-underline border-0 bg-white"
              onClick={() => scrollToReviewSection()}
            >
              ดูรีวิว
            </button>
          </div>
          {productDetail.brand.length > 0 ? (
            <p className="small">
              ดูสินค้าทั้งหมดของแบรนด์
              <Link href={`/brands/${productDetail.brand[0].urlkey}`}>
                <a className="text-red mx-1">{productDetail.brand[0].label}</a>
              </Link>
            </p>
          ) : (
            ""
          )}

          {isFlashSale ? <FlashSale /> : ""}

          {isFixPriceMode ? (
            <div className="product-price">
              <span className="price">
                ฿{numberToMoneyString(specialPriceValue, 0)}.-
              </span>
              {isSpecialPrice ? (
                <del className="mx-2">
                  ฿{numberToMoneyString(fixPriceValue, 0)}.-
                </del>
              ) : (
                ""
              )}

              <div className="on-sale">
                {isSpecialPrice ? <span>Off {percentOff}%</span> : ""}

                <small className="mx-1">(include Vat)</small>
              </div>
            </div>
          ) : (
            // Price range mode
            <div className="product-price">
              <span className="price">
                ฿{numberToMoneyString(minPriceValue, 0)} - ฿
                {numberToMoneyString(maxPriceValue, 0)}
              </span>
            </div>
          )}

          <p className="coins">
            <i className="icon icon-coin" />
            รับ Topvalue Coin สูงสุดถึง{" "}
            <a className="text-red">{productDetail.coin}</a>
          </p>
          <div>
            <ul className="product-info-list">
              <li>
                <div className="col-left">
                  <p className="mt-md-2">
                    <b>การรับประกัน</b>
                  </p>
                </div>
                <div className="col-right">
                  <WarrantyInfo warranties={productDetail.warranty} />
                </div>
              </li>
              <li>
                <div className="col-left">
                  <p>
                    <b>การจัดส่ง</b>
                  </p>
                </div>
                <div className="col-right">
                  <DeliveryOptions deliveries={productDetail.shipping} />
                </div>
              </li>
              {productDetail.special_price >= 3000 && (
                <li>
                  <div className="col-left">
                    <p className="mb-2 mt-md-2">
                      <b>รูปแบบการจ่าย</b>
                    </p>
                  </div>
                  <div className="col-right">
                    <PaymentOptions />
                  </div>
                </li>
              )}

              {isConfigurable ? (
                <li className="d-none d-lg-block">
                  <div className="col-left">
                    <p>
                      <b>{productDetail.customizes[0].title}</b>
                    </p>
                  </div>
                  <div className="col-right">
                    <div className="product-color-switch">
                      {productDetail.customizes[0].values.map(
                        (varient, key) => (
                          <ProductConfiguration
                            key={key}
                            varient={varient}
                            selectedVarientSku={selectedVarientSku}
                            isChecked={
                              varient.sku === selectedVarientSku ? true : false
                            }
                            onSelectedVarientChange={
                              handleSelectedVarientChanged
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </li>
              ) : (
                ""
              )}
              <li>
                <div className="col-left">
                  <p>
                    <b>แชร์</b>
                  </p>
                </div>
                <div className="col-right">
                  <SocialShare
                    productKey={productKey}
                    productDetail={productDetail}
                  />
                </div>
              </li>
              <li className="d-none d-lg-block">
                <div className="col-left">
                  <div>
                    <div className="col-right">
                      <AddButtonSet
                        selectedQty={productselectedQty}
                        onQtyChange={handleProductselectedQtyChanged}
                        onAddtoCart={handleAddtoCart}
                        isWishlist={isWishlist}
                        onUpdateWishList={handleUpdateWishlist}
                        isLoading={isAddtoCartLoading}
                        isSaleable={isSaleable}
                        isWishlistLoading={isWishlistLoading}
                      />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Handle price product configuration */}
      {isFixPriceMode ? (
        <>
          {isShowAddtoCartModal ? (
            <AddToCartModal
              onClose={handleCloseModal}
              productTitle={productDetail.name}
              price={fixPriceValue}
              specialPrice={specialPriceValue}
              qty={productSelectedQty}
              sku={selectedVarientSku}
              imageUrl={productImages[0] && productImages[0].url}
            />
          ) : (
            ""
          )}
        </>
      ) : (
        <>
          {isShowAddtoCartModal ? (
            <AddToCartModal
              onClose={handleCloseModal}
              productTitle={productDetail.name}
              price={productDetail.price}
              specialPrice={productDetail.special_price}
              qty={productSelectedQty}
              sku={selectedVarientSku}
              imageUrl={productImages[0] && productImages[0].url}
            />
          ) : (
            ""
          )}
        </>
      )}

      {/* ProductViewNavigate */}
      <ProductViewNavigate
        isConfigurable={isConfigurable}
        selectedVarientSku={selectedVarientSku}
        handleSelectedVarientChanged={handleSelectedVarientChanged}
        productDetail={productDetail}
        productselectedQty={productselectedQty}
        handleProductselectedQtyChanged={handleProductselectedQtyChanged}
        handleAddtoCart={handleAddtoCart}
        isWishlist={isWishlist}
        onUpdateWishList={handleUpdateWishlist}
        isAddtoCartLoading={isAddtoCartLoading}
        isSaleable={isSaleable}
        isWishlistLoading={isWishlistLoading}
        isShowCanvas={showCanvas}
        handleShowCanvas={handleShowCanvas}
        handleCloseCanvas={handleCloseCanvas}
        fixPriceValue={fixPriceValue}
        specialPriceValue={specialPriceValue}
        imageUrl={productImages[0] && productImages[0].url}
      />
    </>
  );
};

export default ProductInfo;
